import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CustomLayout.scss";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ApartmentOutlined,
  BankOutlined,
  BellOutlined,
  CalendarOutlined,
  ContainerOutlined,
  DashboardOutlined,
  DownloadOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ProductOutlined,
  SearchOutlined,
  TagOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Input, Layout, Menu, Modal, theme, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getApi, postApi, patchApi } from "../../Utlis/apiService";
import { GET_NOTIFICATION_COUNTS, APP_TOGGLE_REQUEST_OTP, APP_TOGGLE_VERIFY, APP_TOGGLE_CURRENT } from "../../Utlis/apiPath";
import { successToast, errorToast } from "../../Services/ToastHelper";
import CompanyLogo from "../../Assets/Images/CompanyLogo.jpg";
import { setCurrentMode } from "../../redux/appModeSlice";

const { Header, Sider, Content } = Layout;

const CustomLayout = ({ children }) => {
  const [selectedKey, setSelectedKey] = useState("1");
  const [openKeys, setOpenKeys] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isLogoutModalVisible, setLogoutModalVisible] = useState(false);
  const [isNotificationOpen, setNotificationOpen] = useState(false);
  const [freeSessionCount, setFreeSessionCount] = useState(0);
  const [enquiryCount, setEnquiryCount] = useState(0);

  // ── Application toggle ─────────────────────────────────
  const dispatch = useDispatch();
  const currentMode = useSelector((state) => state.appMode.currentMode);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [pendingMode, setPendingMode] = useState(null);
  const [requestOtpLoading, setRequestOtpLoading] = useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);

  // ── Drag-to-resize sidebar ─────────────────────────────
  const [siderWidth, setSiderWidth] = useState(270);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);
  const onMouseMoveRef = useRef(null);
  const onMouseUpRef = useRef(null);

  const handleDragStart = (e) => {
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = siderWidth;

    onMouseMoveRef.current = (moveEvt) => {
      const diff = moveEvt.clientX - dragStartXRef.current;
      const next = Math.min(Math.max(dragStartWidthRef.current + diff, 220), 420);
      setSiderWidth(next);
    };

    onMouseUpRef.current = () => {
      document.removeEventListener("mousemove", onMouseMoveRef.current);
      document.removeEventListener("mouseup", onMouseUpRef.current);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMoveRef.current);
    document.addEventListener("mouseup", onMouseUpRef.current);
  };

  useEffect(() => {
    return () => {
      if (onMouseMoveRef.current) document.removeEventListener("mousemove", onMouseMoveRef.current);
      if (onMouseUpRef.current) document.removeEventListener("mouseup", onMouseUpRef.current);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const savedKey = localStorage.getItem("selectedMenuKey");
    if (savedKey) {
      setSelectedKey(savedKey);
      // Open the parent submenu of the saved key on first load
      const parentMap = {
        "3": "sub4", "18": "sub4",
        "4": "sub1",
        "15": "sub3", "16": "sub3",
        "12": "sub2", "13": "sub2", "14": "sub2",
      };
      const parent = parentMap[savedKey];
      if (parent) setOpenKeys([parent]);
    }
  }, []);

  const fetchNotificationCounts = async () => {
    try {
      const response = await getApi(GET_NOTIFICATION_COUNTS);
      if (response && response.status === 200) {
        const freeSessionRequestCount = response.data?.freeSessionRequestCount ?? 0;
        setFreeSessionCount(freeSessionRequestCount);
        setEnquiryCount(response.data?.enquiryCount ?? 0);
        localStorage.setItem("freeSessionCount", String(freeSessionRequestCount));
      }
    } catch {
      // silently ignore — badges just won't show
    }
  };

  // Re-fetch on every page navigation, in addition to on mount
  useEffect(() => {
    fetchNotificationCounts();
  }, [location.pathname]);

  useEffect(() => {
    const fetchCurrentMode = async () => {
      try {
        const response = await getApi(APP_TOGGLE_CURRENT);
        if (response && response.success && response.mode) {
          dispatch(setCurrentMode(response.mode));
        }
      } catch { /* silently ignore */ }
    };
    fetchCurrentMode();
  }, []);

  const handleToggleClick = async () => {
    const targetMode = currentMode === "daylight" ? "standard" : "daylight";
    setPendingMode(targetMode);
    setRequestOtpLoading(true);
    try {
      const adminEmail = localStorage.getItem("adminEmail") || "";
      const response = await postApi(APP_TOGGLE_REQUEST_OTP, { adminId: 0, adminEmail });
      if (response && (response.status === 200 || response.message)) {
        setOtpModalOpen(true);
        setOtpValue("");
        successToast(response.message || "OTP sent to your registered email");
      } else {
        errorToast(response?.message || "Failed to send OTP");
      }
    } catch {
      errorToast("Failed to request OTP");
    } finally {
      setRequestOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue.trim()) return;
    setVerifyOtpLoading(true);
    try {
      const response = await patchApi(APP_TOGGLE_VERIFY, { otp: otpValue, mode: pendingMode });
      if (response && (response.status === 200 || response.message)) {
        dispatch(setCurrentMode(pendingMode));
        setOtpModalOpen(false);
        setOtpValue("");
        successToast(response.message || `Switched to ${pendingMode} mode`);
      } else {
        errorToast(response?.message || "Invalid OTP");
      }
    } catch {
      errorToast("OTP verification failed");
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isNotificationOpen && !event.target.closest('.notification-container')) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen]);

  const handleMenuClick = (menuKey) => {
    setSelectedKey(menuKey);
    localStorage.setItem("selectedMenuKey", menuKey);

    const pathMap = {
      1: "/dashboard",
      2: "/category-management",
      3: "/standard-class",
      4: "/booking",
      5: "/instructor",
      15: "/students",
      7: "/calender",
      8: "/staff-management",
      9: "/reschedule",
      10: "/enquiry",
      11: "/Freessession-Requests",
      12: "/bank-details",
      13: "/payouts",
      14: '/expenses',
      16: '/lead_student',
      17: '/add-student',
      18: '/day-light-class',
      19: '/coupons',
    };
    const path = pathMap[menuKey];
    if (path) {
      navigate(path);
    }
    if (menuKey === "11") {
      setFreeSessionCount(0);
    }
  };

  useEffect(() => {
    fetch("http://ip-api.com/json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {})
      .catch((error) => {
        console.error("Error fetching IP location:", error);
      });
  }, []);

  const handleLogout = () => {
    navigate("/");
    setLogoutModalVisible(false);
    // Add your logout logic here
  };

  const showLogoutModal = () => {
    setLogoutModalVisible(true);
  };

  const closeLogoutModal = () => {
    setLogoutModalVisible(false);
  };

  const toggleNotification = () => {
    setNotificationOpen(!isNotificationOpen);
  };

  // Small S/D pill shown beside whichever class row matches the active mode
  const modeTogglePill = (
    <span
      onClick={(e) => {
        e.stopPropagation();
        if (!requestOtpLoading) handleToggleClick();
      }}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        flexShrink: 0,
        padding: "2px",
        border: "1.5px solid #334155",
        borderRadius: 13,
        background: "#fff",
        cursor: requestOtpLoading ? "not-allowed" : "pointer",
        opacity: requestOtpLoading ? 0.6 : 1,
        pointerEvents: "auto",
      }}
    >
      <span
        style={{
          width: 17,
          height: 17,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 8,
          fontWeight: 700,
          background: currentMode !== "daylight" ? "#f5a623" : "transparent",
          color: currentMode !== "daylight" ? "#fff" : "#9ca3af",
        }}
      >
        S
      </span>
      <span
        style={{
          width: 17,
          height: 17,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 8,
          fontWeight: 700,
          background: currentMode === "daylight" ? "#f5a623" : "transparent",
          color: currentMode === "daylight" ? "#fff" : "#9ca3af",
        }}
      >
        D
      </span>
    </span>
  );

  const menuItems = useMemo(() => [
    {
      key: "1",
      icon: <DashboardOutlined />,
      label: "Dashboard",
    },
    {
      key: "2",
      icon: <ContainerOutlined />,
      label: "Category Management",
    },
    {
      key: "sub4",
      icon: <ApartmentOutlined />,
      label: "Class Management",
      children: [
        {
          key: "3",
          className: "class-mgmt-toggle-host",
          label: (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span>Standard Class</span>
              {currentMode !== "daylight" && modeTogglePill}
            </span>
          ),
        },
        {
          key: "18",
          className: "class-mgmt-toggle-host",
          label: (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span>Day Light Class</span>
              {currentMode === "daylight" && modeTogglePill}
            </span>
          ),
        },
      ],
    },
    {
      key: "sub1",
      icon: <DownloadOutlined />,
      label: "Bookings",
      children: [
        { key: "4", label: "Booking" },
      ],
    },
    {
      key: "5",
      icon: <UserOutlined />,
      label: "Instructor",
    },
    {
      key: "sub3",
      icon: <UsergroupAddOutlined />,
      label: "Students",
      children: [
        { key: "15", label: "Enrolled Students" },
        { key: "16", label: "Lead Students" },
      ],
    },
    {
      key: "7",
      icon: <CalendarOutlined />,
      label: "Calendar",
    },
    {
      key: "8",
      icon: <ContainerOutlined />,
      label: "Staff Management",
    },
    {
      key: "9",
      icon: <UserOutlined />,
      label: "Reschedule",
    },
    {
      key: "10",
      icon: (
        <span style={{ position: "relative", display: "inline-flex" }}>
          <UsergroupAddOutlined />
          {enquiryCount > 0 && (
            <span style={{
              position: "absolute",
              top: -6,
              right: -8,
              background: "#ff4d4f",
              color: "#fff",
              borderRadius: "50%",
              width: 14,
              height: 14,
              fontSize: 9,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}>
              {enquiryCount > 9 ? "9+" : enquiryCount}
            </span>
          )}
        </span>
      ),
      label: "Enquiry",
    },
    {
      key: "11",
      icon: (
        <span style={{ position: "relative", display: "inline-flex" }}>
          <CalendarOutlined />
          {freeSessionCount > 0 && (
            <span style={{
              position: "absolute",
              top: -6,
              right: -8,
              background: "#ff4d4f",
              color: "#fff",
              borderRadius: "50%",
              width: 14,
              height: 14,
              fontSize: 9,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}>
              {freeSessionCount > 9 ? "9+" : freeSessionCount}
            </span>
          )}
        </span>
      ),
      label: (
        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Free Session Requests</span>
        </span>
      ),
    },
    {
      key: "19",
      icon: <TagOutlined />,
      label: "Coupons",
    },
    {
      key: "sub2",
      icon: <BankOutlined />,
      label: "Finance",
      children: [
        { key: "12", label: "Bank Details" },
        { key: "13", label: "Payouts" },
        { key: "14", label: "Expenses" },
      ],
    },
  ], [freeSessionCount, enquiryCount, currentMode, requestOtpLoading]);

  return (
    <Layout className="layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="sidebar"
        width={siderWidth}
      >
        <div className="sidebar_logo">
          <img src={CompanyLogo} alt="Company Logo" />
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={(keys) => {
            // Accordion: keep only the most-recently opened key
            const latest = keys.find((k) => !openKeys.includes(k));
            setOpenKeys(latest ? [latest] : []);
          }}
          onClick={({ key }) => handleMenuClick(key)}
          items={menuItems}
        />
        {!collapsed && (
          <div className="sidebar-drag-handle" onMouseDown={handleDragStart} />
        )}
      </Sider>
      <Layout>
        <Header
          style={{
            padding: 0,
            background: "#f0f2f5",
          }}
        >
          <div className="header">
            <div className="header_left">
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{
                  fontSize: "16px",
                  width: 64,
                  height: 64,
                }}
              />
            </div>
            <div className="header_right">
              <div className="notification-container">
                <BellOutlined 
                  style={{ fontSize: "20px", cursor: "pointer" }} 
                  onClick={toggleNotification}
                />
                {isNotificationOpen && (
                  <div className="notification-dropdown">
                    <div className="notification-header">
                      <h4>Notifications</h4>
                    </div>
                    <div className="notification-content">
                      <p>No notifications available</p>
                    </div>
                  </div>
                )}
              </div>
              <div
                className="header_right_user"
                style={{ cursor: "pointer" }}
                onClick={showLogoutModal}
              >
                <h6>J</h6>
              </div>
            </div>
          </div>
          <Modal
            title="Logout Confirmation"
            open={isLogoutModalVisible}
            onOk={handleLogout}
            onCancel={closeLogoutModal}
            okText="Logout"
            cancelText="Cancel"
            width={300}
          >
            <p>Are you sure you want to log out?</p>
          </Modal>

          <Modal
            title={`Switch to ${pendingMode === "daylight" ? "Daylight" : "Standard"} Mode`}
            open={otpModalOpen}
            onCancel={() => { setOtpModalOpen(false); setOtpValue(""); }}
            onOk={handleVerifyOtp}
            okText="Verify & Switch"
            okButtonProps={{ loading: verifyOtpLoading, disabled: !otpValue.trim() }}
            cancelText="Cancel"
            width={480}
            destroyOnClose
          >
            <p style={{ marginBottom: 12, color: "#6b7280", fontSize: 13 }}>
              An OTP has been sent to your registered email. Enter it below to confirm the mode switch.
            </p>
            <Input
              placeholder="Enter OTP"
              value={otpValue}
              onChange={(e) => setOtpValue(e.target.value)}
              onPressEnter={handleVerifyOtp}
              maxLength={10}
              style={{ letterSpacing: "0.2em", fontSize: 18, textAlign: "center" }}
              autoFocus
            />
          </Modal>
        </Header>
        <Content
          style={{
            // margin: "24px 16px",
            padding: 24,
            minHeight: 280,
            background: "#f5f5f5",
            borderRadius: borderRadiusLG,
            overflowY: "auto",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default CustomLayout;